import { memo } from "react"
import { FiEye, FiTrash2 } from "react-icons/fi"

import type { AttendanceResponse } from "@/types/attendance"
import {
  Button,
  Flex,
  Heading,
  IconButton,
  Separator,
  Stack,
  Text,
} from "@chakra-ui/react"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

interface AttendanceCardProps {
  attendance: AttendanceResponse
  onEdit?: (attendance: AttendanceResponse) => void
  onDelete?: (attendance: AttendanceResponse) => void
  onView?: (attendance: AttendanceResponse) => void
}

const AttendanceCard = memo(
  ({ attendance, onEdit, onDelete, onView }: AttendanceCardProps) => {
    const titleValue = attendance.enrollment_id ?? attendance.id

    const subtitleValue = attendance.class_session_id ?? undefined

    return (
      <Stack
        borderWidth="1px"
        borderRadius="lg"
        padding={4}
        gap={4}
        bg="bg.surface"
        shadow="sm"
      >
        <Stack gap={1}>
          <Heading size="sm">{titleValue}</Heading>
          {subtitleValue !== undefined && (
            <Text color="fg.muted" fontSize="sm">
              {subtitleValue || "No description"}
            </Text>
          )}
        </Stack>

        <Stack gap={1} fontSize="xs" color="fg.subtle">
          <Text>
            Created: {new Date(attendance.created_at).toLocaleString()}
          </Text>
          <Text>
            Updated: {new Date(attendance.updated_at).toLocaleString()}
          </Text>
        </Stack>

        <Separator />
        <Stack gap={2} fontSize="sm">
          <Flex
            key="enrollment_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Enrollment Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.enrollment_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="class_session_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Class Session Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.class_session_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="student_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Student Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.student_id, "str")}
            </Text>
          </Flex>
          <Flex
            key="class_date"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Class Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.class_date, "date")}
            </Text>
          </Flex>
          <Flex
            key="class_time"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Class Time</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.class_time, "str")}
            </Text>
          </Flex>
          <Flex key="status" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Status</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.status, "str")}
            </Text>
          </Flex>
          <Flex
            key="check_in_time"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Check In Time</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.check_in_time, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="check_out_time"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Check Out Time</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.check_out_time, "datetime")}
            </Text>
          </Flex>
          <Flex
            key="is_makeup_class"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Is Makeup Class</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.is_makeup_class, "bool")}
            </Text>
          </Flex>
          <Flex
            key="makeup_for_date"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Makeup For Date</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.makeup_for_date, "date")}
            </Text>
          </Flex>
          <Flex
            key="instructor_notes"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Instructor Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.instructor_notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="skills_practiced"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Skills Practiced</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.skills_practiced, "dict")}
            </Text>
          </Flex>
          <Flex
            key="behavior_notes"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Behavior Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.behavior_notes, "str")}
            </Text>
          </Flex>
          <Flex
            key="parent_notification_sent"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Parent Notification Sent</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.parent_notification_sent, "bool")}
            </Text>
          </Flex>
          <Flex
            key="marked_by_user_id"
            justify="space-between"
            gap={4}
            align="flex-start"
          >
            <Text fontWeight="medium">Marked By User Id</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.marked_by_user_id, "str")}
            </Text>
          </Flex>
          <Flex key="notes" justify="space-between" gap={4} align="flex-start">
            <Text fontWeight="medium">Notes</Text>
            <Text textAlign="right" flex="1" color="fg.muted">
              {formatFieldValue(attendance.notes, "str")}
            </Text>
          </Flex>
        </Stack>

        <Flex gap={2} justify="flex-end">
          {onView && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onView(attendance)}
            >
              <FiEye /> View
            </Button>
          )}
          {onEdit && (
            <Button
              size="sm"
              variant="outline"
              colorScheme="blue"
              onClick={() => onEdit(attendance)}
            >
              Edit
            </Button>
          )}
          {onDelete && (
            <IconButton
              size="sm"
              variant="ghost"
              colorScheme="red"
              aria-label="Delete attendance"
              onClick={() => onDelete(attendance)}
            >
              <FiTrash2 />
            </IconButton>
          )}
        </Flex>
      </Stack>
    )
  },
)

AttendanceCard.displayName = "AttendanceCard"

export default AttendanceCard
